/**
 * SafeImage — the fast, uniform preview-image framework for Deskey.
 *
 * Why it exists
 * ─────────────
 * Editor topics can carry 10+ heavy base64 PNGs (charts, drawings,
 * AI-generated images, 200KB+ each). Rendering them all naively can
 * block the main thread. This component keeps rendering cheap while
 * guaranteeing the image is ALWAYS mounted:
 *
 *   1. **Native lazy-load + async decode** — the <img> carries
 *      loading="lazy" + decoding="async" so off-screen images defer
 *      their fetch/decode without any JS gating. (An earlier version
 *      gated the <img> behind an IntersectionObserver; on a zero-height
 *      wrapper the observer's 0.01 threshold could never fire, so the
 *      <img> stayed unmounted forever and the preview collapsed to a
 *      one-pixel line. The native attributes give the same perf win
 *      without that deadlock.)
 *
 *   2. **Skeleton shimmer** while decoding — reserves the slot with a
 *      min-height so the layout doesn't jump and the shimmer is visible.
 *
 *   3. **Click-to-lightbox** — every preview thumbnail becomes tappable
 *      and opens a zoomable fullscreen viewer (PreviewLightbox).
 *      Default `zoomable=true`, opt out with `zoomable={false}`.
 *
 * Extra guarantees
 *  • decoding="async" + loading="lazy" so decode never blocks paint.
 *  • Graceful onError / invalid src → visible "Image unavailable" fallback
 *    (never a silent zero-height collapse — that hid broken images behind
 *    an unreachable Remove button).
 *  • Backwards-compat with the old signature (src, alt, className,
 *    wrapClass, caption, onReady, skeleton, eager).
 */
import React, { useState, useCallback } from 'react';
import PreviewLightbox from './PreviewLightbox';

/** Cheap check — a data:image/... URL or an http(s) URL that isn't empty */
function isValidSrc(src) {
  if (typeof src !== 'string' || src.length <= 4) return false;
  // LLM-scrubbed payloads round-tripped into a draft look like "<image elided>"
  if (src.includes('image elided')) return false;
  // Only scan the head for a botched template ("data:undefined;..." /
  // "https://x/undefined") — a megabyte of base64 can legitimately
  // contain the letters "undefined" and must not be rejected for it.
  return !src.slice(0, 128).includes('undefined');
}

export default function SafeImage({
  src,
  alt = '',
  className = '',
  wrapClass = '',
  caption = null,
  onReady,
  skeleton = true,
  zoomable = true,
  eager = false,           // set true for above-the-fold heroes → loading="eager"
  onClick,                 // if provided, overrides default lightbox behaviour
  ...rest
}) {
  const valid = isValidSrc(src);

  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleClick = useCallback((e) => {
    if (onClick) return onClick(e);
    if (zoomable && loaded && !errored) setLightboxOpen(true);
  }, [onClick, zoomable, loaded, errored]);

  if (!valid || errored) {
    // Visible fallback instead of returning null. The old contract
    // ("graceful onError → null, parent gets the collapse") made a broken
    // payload indistinguishable from a missing feature: the card collapsed
    // to a border line, and the overlaid Remove button collapsed with it so
    // the teacher couldn't even delete the bad entry to regenerate it.
    return (
      <figure className={wrapClass} data-testid="safeimage-fallback">
        <div className="w-full min-h-[96px] flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400 px-4 py-6">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-3.9-3.9a2 2 0 0 0-2.8 0L6 19.5" /><line x1="3" y1="3" x2="21" y2="21" />
          </svg>
          <span className="text-[11px] font-semibold">Image unavailable</span>
          <span className="text-[10px]">Remove this image and generate it again</span>
        </div>
      </figure>
    );
  }

  const clickable = zoomable || !!onClick;

  return (
    <>
      <figure className={wrapClass}>
        {/* Reserve height while decoding so the skeleton is visible and the
            layout doesn't jump; collapses to the image's natural box on load. */}
        <div className="relative" style={!loaded && skeleton ? { minHeight: 120 } : undefined}>
          {/* Skeleton shimmer — sits behind the image until it's decoded */}
          {skeleton && !loaded && (
            <div
              className="absolute inset-0 rounded-lg overflow-hidden bg-slate-100 dark:bg-white/[0.04]"
              aria-hidden="true"
              data-testid="safeimage-skeleton"
            >
              <div className="w-full h-full animate-pulse bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-white/[0.06]" />
            </div>
          )}

          <img
            src={src}
            alt={alt}
            className={
              (className || '') +
              ' block transition-opacity duration-300 ' +
              (loaded ? 'opacity-100' : 'opacity-0') +
              (clickable ? ' cursor-zoom-in' : '')
            }
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={() => { setLoaded(true); onReady?.(true); }}
            onError={() => { setErrored(true); onReady?.(false); }}
            onClick={clickable ? handleClick : undefined}
            data-testid="safeimage-img"
            {...rest}
          />
        </div>

        {caption && (
          <figcaption className="mt-2 text-center text-[12px] text-slate-500 italic px-2">
            {caption}
          </figcaption>
        )}
      </figure>

      {lightboxOpen && (
        <PreviewLightbox
          src={src}
          alt={alt}
          caption={typeof caption === 'string' ? caption : ''}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
